/**
 * nodeSync — ĐỒNG BỘ DANH SÁCH NODE giữa BACKEND và CLIENT.
 *
 * VẤN ĐỀ: danh sách node ở client (thư mục components/flow) là một mảng CỨNG. Máy chủ thì lại chỉ có
 * những node do gói đã cài cung cấp (aianalysis cần gói agent; nhóm facebook - zalo - adssync cần gói
 * marketing; node của usermodule thì có/không tuỳ công ty). Hệ quả cũ:
 *   • Client vẽ ra node mà máy chủ KHÔNG có -> kéo vào flow, chạy là lỗi "Node 'x' cần gói ...".
 *   • Máy chủ có node MỚI (gói mới, node do usermodule đăng ký) -> client không biết, không dùng được.
 *
 * NGUYÊN TẮC ĐỒNG BỘ (một câu):
 *   BACKEND quyết định node nào TỒN TẠI; CLIENT quyết định node đó HIỂN THỊ/NHẬP LIỆU thế nào.
 *
 * Vì sao không lấy hẳn một bên?
 *   • Spec bên backend đi qua HTTP nên KHÔNG mang được hàm — mà form của client sống nhờ hàm:
 *     `onChange` (chọn loại chứng từ -> nạp lại form điều kiện), `showIf` dạng hàm, `component` vẽ node.
 *     Đó chính là "một số thuộc tính điều khiển form" mà backend còn thiếu.
 *   • Ngược lại chỉ tin client thì lại quay về đúng bệnh cũ: hiện node máy chủ không có.
 * Nên: LẤY DANH SÁCH từ backend, LẤY CÁCH ĐIỀU KHIỂN FORM từ client, trộn theo từng trường.
 *
 * Node backend trả về mà client CHƯA khai báo -> dựng "node động": vẫn kéo thả, vẫn cấu hình được
 * bằng chính metadata backend gửi kèm, chỉ là dùng giao diện mặc định thay cho component riêng.
 *
 * Đây là file THUẦN (không React, không mạng) để kiểm thử được độc lập.
 */

/**
 * Nhóm (category) bên backend viết bằng thuật ngữ kỹ thuật tiếng Anh (Input/Logic/Function/Output/
 * Network), còn thanh bên trái của client gom theo nghiệp vụ tiếng Việt. Node CÓ SẴN ở client thì lấy
 * nhóm của client nên không sao; nhưng node ĐỘNG (chỉ backend có) mà để nguyên sẽ đẻ ra những nhóm
 * "Logic", "Output" lạc lõng bên cạnh "Xử lý & Logic", "Kết nối mở rộng". Quy về một mối:
 */
const CATEGORY_ALIASES = {
	input: 'Kích hoạt', trigger: 'Kích hoạt',
	logic: 'Xử lý & Logic', function: 'Xử lý & Logic', ai: 'Xử lý & Logic', processing: 'Xử lý & Logic',
	output: 'Kết nối mở rộng', network: 'Kết nối mở rộng', integration: 'Kết nối mở rộng',
	marketing: 'Kết nối mở rộng',
	company: 'Công ty',
};

export function normalizeCategory(category) {
	if (!category) return 'Khác';
	return CATEGORY_ALIASES[String(category).trim().toLowerCase()] || category;
}

/** Ưu tiên giá trị của client, nhưng KHÔNG để `undefined` của client xoá mất giá trị của backend. */
function pick(clientValue, serverValue) {
	return clientValue === undefined || clientValue === null || clientValue === '' ? serverValue : clientValue;
}

/**
 * Chuẩn hoá một trường metadata do BACKEND gửi sang đúng "phương ngữ" của form client.
 * Backend viết `helpText`, client đọc `helper`; backend có thể quên `type` (mặc định là ô chữ).
 */
export function normalizeServerField(f) {
	if (!f || typeof f !== 'object') return null;
	const out = { ...f };
	if (out.helper === undefined && out.helpText !== undefined) out.helper = out.helpText;
	if (!out.type) out.type = 'text';
	if (Array.isArray(out.fields)) out.fields = out.fields.map(normalizeServerField).filter(Boolean);
	return out;
}

/** Chuẩn hoá một node do backend gửi (endpoint có 2 dạng: rút gọn `input/output` và đầy đủ `*Description`). */
export function normalizeServerNode(n) {
	if (!n || !n.type) return null;
	return {
		...n,
		type: String(n.type),
		inputDescription: n.inputDescription || n.input || '',
		outputDescription: n.outputDescription || n.output || '',
		metadata: Array.isArray(n.metadata) ? n.metadata.map(normalizeServerField).filter(Boolean) : [],
	};
}

/**
 * Trộn metadata theo TỪNG TRƯỜNG.
 *  - Thứ tự trường: theo client (thứ tự đã được thiết kế cho người dùng), trường chỉ có ở backend
 *    được thêm vào CUỐI (backend biết node cần gì mà client chưa cập nhật).
 *  - Trong một trường: nền là bản backend, đắp bản client lên trên (client có hàm onChange/showIf,
 *    có ref_model để mở danh mục, có nhãn tiếng Việt sát nghiệp vụ hơn).
 */
export function mergeMetadata(clientFields, serverFields) {
	const cList = Array.isArray(clientFields) ? clientFields : [];
	const sList = Array.isArray(serverFields) ? serverFields.map(normalizeServerField).filter(Boolean) : [];
	if (!cList.length) return sList;
	if (!sList.length) return cList;

	const sByName = new Map(sList.map((f) => [f.name, f]));
	const used = new Set();

	const merged = cList.map((cf) => {
		const sf = sByName.get(cf.name);
		if (!sf) return cf;                 // trường chỉ client có (thường là helpText hướng dẫn) -> giữ
		used.add(cf.name);
		const out = { ...sf };
		for (const k of Object.keys(cf)) if (cf[k] !== undefined) out[k] = cf[k];
		return out;
	});

	// Trường backend khai thêm mà client chưa có -> vẫn phải cho người dùng nhập, không thì node chạy thiếu.
	for (const sf of sList) if (!used.has(sf.name)) merged.push({ ...sf, __serverOnly: true });
	return merged;
}

/** Node client + node backend cùng type -> một định nghĩa dùng được. */
export function mergeNode(clientNode, serverNode) {
	const s = normalizeServerNode(serverNode) || {};
	const c = clientNode || {};
	return {
		...s,
		...c,
		type: s.type || c.type,
		label: pick(c.label, s.label),
		category: pick(c.category, s.category),
		description: pick(c.description, s.description),
		inputDescription: pick(c.inputDescription, s.inputDescription),
		outputDescription: pick(c.outputDescription, s.outputDescription),
		metadata: mergeMetadata(c.metadata, s.metadata),
		__server: true,
	};
}

/**
 * Node backend có mà client CHƯA khai báo -> node ĐỘNG.
 * Không có component riêng (dùng giao diện mặc định) nhưng vẫn đủ metadata để cấu hình.
 */
export function toDynamicNode(serverNode) {
	const s = normalizeServerNode(serverNode);
	if (!s) return null;
	return {
		...s,
		label: s.label || s.type,
		category: normalizeCategory(s.category),
		component: undefined,     // -> DefaultNode, xem createNodeRegistry trong nodes.js
		__server: true,
		__dynamic: true,          // để SideBar/PropertiesBar ghi chú "node do máy chủ cung cấp"
	};
}

/**
 * TRỘN danh sách.
 * @param {Array} clientNodes  khai báo tĩnh trong components/flow/nodes.js
 * @param {Array|null} serverNodes  kết quả GET /api/:id_app/flow/nodes?full=1 (null = chưa hỏi được)
 * @returns {{nodes:Array, all:Array, hidden:Array<string>, dynamic:Array<string>, fromServer:boolean}}
 *   nodes = danh sách CHO PHÉP THÊM MỚI (thanh bên trái, kiểm tra hợp lệ trước khi chạy).
 *   all   = nodes + những node client có mà máy chủ KHÔNG có, đánh dấu `__unavailable`.
 *           Vì sao cần: một flow ĐÃ LƯU từ trước có thể chứa node của gói vừa bị gỡ. Nếu bỏ hẳn khỏi
 *           map component thì ReactFlow vẽ ra một ô trắng vô nghĩa, người dùng không mở ra sửa hay xoá
 *           được. Giữ lại để VẼ (không cho thêm mới) là cách duy nhất để họ dọn flow cũ.
 *
 * CHƯA HỎI ĐƯỢC (máy chủ đời cũ, mất mạng, endpoint lỗi) -> trả nguyên danh sách client.
 * Thà hiện thừa vài node còn hơn khoá trắng màn hình soạn flow của người dùng.
 */
export function mergeNodeDefinitions(clientNodes, serverNodes) {
	const cList = Array.isArray(clientNodes) ? clientNodes : [];
	if (!Array.isArray(serverNodes) || serverNodes.length === 0) {
		return { nodes: cList, all: cList, hidden: [], dynamic: [], fromServer: false };
	}

	const sList = serverNodes.map(normalizeServerNode).filter(Boolean);
	const sByType = new Map(sList.map((n) => [n.type, n]));
	const cByType = new Map(cList.filter((n) => n && n.type).map((n) => [n.type, n]));

	const nodes = [];
	const unavailable = [];
	const hidden = [];
	const dynamic = [];

	// 1) Giữ THỨ TỰ của client (đã nhóm theo nghiệp vụ) — chỉ những node máy chủ THẬT SỰ có.
	for (const c of cList) {
		if (!c || !c.type) continue;
		const s = sByType.get(c.type);
		if (!s) {                                    // máy chủ không có -> KHÔNG cho thêm mới nữa,
			hidden.push(c.type);                     // nhưng vẫn giữ để vẽ được flow cũ đang dùng nó.
			unavailable.push({ ...c, __unavailable: true });
			continue;
		}
		nodes.push(mergeNode(c, s));
	}

	// 2) Node máy chủ có mà client chưa biết -> node động, xếp cuối.
	for (const s of sList) {
		if (cByType.has(s.type)) continue;
		const d = toDynamicNode(s);
		if (d) { nodes.push(d); dynamic.push(d.type); }
	}

	return { nodes, all: nodes.concat(unavailable), hidden, dynamic, fromServer: true };
}

export default mergeNodeDefinitions;
